/* ============================================================
   Biotiful Life — <seed-sort>
   Progressive enhancement that turns Dawn's native "Sort by"
   <select> into a branded dropdown matching the filter panels.
   The native <select> is kept in the DOM and stays the source of
   truth: choosing an option sets its value and fires `input`,
   which Dawn's <facet-filters-form> already listens for, so the
   existing AJAX sort keeps working untouched. Degrades to the
   native select if JS is off. Re-upgrades automatically after a
   Section-Rendering refresh (the tag comes back in the new HTML).
   ============================================================ */
(() => {
  if (customElements.get('seed-sort')) return;

  const CARET =
    "<svg class='seed-sort__caret' width='11' height='11' viewBox='0 0 12 12' aria-hidden='true' focusable='false'>" +
    "<path d='M2.5 4.25 6 7.75l3.5-3.5' fill='none' stroke='currentColor' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/></svg>";

  class SeedSort extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select || this.dataset.enhanced) return;
      this.dataset.enhanced = 'true';
      this.open = false;
      this.options = Array.from(this.select.options);
      this.render();
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
    }

    render() {
      const current = this.select.options[this.select.selectedIndex];

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'seed-sort__trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.innerHTML =
        "<span class='seed-sort__current'></span>" + CARET;
      this.trigger.querySelector('.seed-sort__current').textContent = current
        ? current.textContent.trim()
        : '';

      this.panel = document.createElement('div');
      this.panel.className = 'seed-sort__panel';
      this.panel.setAttribute('role', 'listbox');

      const list = document.createElement('ul');
      list.className = 'seed-sort__list';
      this.optionEls = this.options.map((opt) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'seed-sort__option';
        btn.setAttribute('role', 'option');
        btn.dataset.value = opt.value;
        btn.textContent = opt.textContent.trim();
        if (opt.selected) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-selected', 'true');
        }
        btn.addEventListener('click', () => this.choose(opt.value));
        li.appendChild(btn);
        list.appendChild(li);
        return btn;
      });
      this.panel.appendChild(list);

      this.classList.add('seed-sort--ready');
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      this._onDocClick = (e) => {
        if (!this.contains(e.target)) this.close();
      };

      this.appendChild(this.trigger);
      this.appendChild(this.panel);
    }

    toggle() {
      this.open ? this.close() : this.show();
    }

    show() {
      this.open = true;
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this._onDocClick);
      const sel = this.panel.querySelector('.is-selected') || this.optionEls[0];
      if (sel) sel.focus();
    }

    close() {
      if (!this.open) return;
      this.open = false;
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this._onDocClick);
    }

    choose(value) {
      if (this.select.value !== value) {
        this.select.value = value;
        // Dawn's facet-filters-form is bound to `input` (debounced); fire both.
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.close();
      this.trigger.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.open) {
        e.preventDefault();
        this.close();
        this.trigger.focus();
        return;
      }
      if (!this.open) return;
      const focusables = this.optionEls;
      const i = focusables.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        (focusables[i + 1] || focusables[0]).focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (focusables[i - 1] || focusables[focusables.length - 1]).focus();
      }
    }
  }

  customElements.define('seed-sort', SeedSort);
})();
